import React, { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "../api";
import "./AdminPanel.css";
import "./Dashboard.css";

// ─── Category colors ─────────────────────────────────────────────────────────
const CAT_COLOR = (cat: string): string => ({
  COMPENSATION:     "#4A90E2",
  OCCUPANCY:        "#e84393",
  OPERATING:        "#ea580c",
  ADVERTISING:      "#7c3aed",
  ALLOCATED:        "#475569",
  AMORT:            "#64748b",
} as Record<string, string>)[cat.toUpperCase().replace(/\s/g, "")] ?? "#94a3b8";

const EXCLUDED_CATS = new Set(["Corp OH", "CORP_OH", "Uncategorized", "OtherAdjustments", "OTHERADJUSTMENTS"]);

// ─── Interfaces ──────────────────────────────────────────────────────────────
interface CCRow {
  cost_center:      string;
  cost_center_name: string;
  business_unit:    string;
  month_budget:   number;
  month_actual:   number;
  month_variance: number;
  ytd_budget:     number;
  ytd_actual:     number;
  ytd_variance:   number;
  qtr_budget:     number;
}

interface CategoryKPI {
  category:       string;
  month_budget:   number;
  month_actual:   number;
  month_variance: number;
  ytd_budget:     number;
  ytd_actual:     number;
  ytd_variance:   number;
  qtr_budget:     number;
  top_over:  CCRow[];
  top_under: CCRow[];
}

interface CCCatBreakdown {
  category:       string;
  month_budget:   number;
  month_actual:   number;
  month_variance: number;
  ytd_budget:     number;
  ytd_actual:     number;
  ytd_variance:   number;
  qtr_budget:     number;
}

interface AcctRow {
  account:        string;
  month_budget:   number;
  month_actual:   number;
  month_variance: number;
  ytd_budget:     number;
  ytd_actual:     number;
  ytd_variance:   number;
}

interface BUCatBreakdown {
  category:       string;
  month_budget:   number;
  month_actual:   number;
  month_variance: number;
  ytd_budget:     number;
  ytd_actual:     number;
  ytd_variance:   number;
  qtr_budget:     number;
}

interface BUSummaryRow {
  business_unit:  string;
  month_budget:   number;
  month_actual:   number;
  month_variance: number;
  ytd_budget:     number;
  ytd_actual:     number;
  ytd_variance:   number;
  qtr_budget:     number;
  categories:     BUCatBreakdown[];
}

interface MonthlyTrendPoint {
  month:      string;
  short:      string;
  budget:     number;
  actual:     number;
  hasActuals: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtMoney(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000)     return `$${Math.round(abs / 1_000)}k`;
  return `$${Math.round(abs)}`;
}

/** Accounting notation: unfavorable (positive variance = over budget) in brackets, favorable plain */
function fmtVar(n: number): string {
  const val = fmtMoney(n);
  return n > 0 ? `(${val})` : val;
}

/** Accounting notation for YTG: negative YTG (over annual budget) in brackets */
function fmtYTG(n: number): string {
  const val = fmtMoney(n);
  return n < 0 ? `(${val})` : val;
}

function getFiscalQuarter(month: string): string {
  const q1 = ["April", "May", "June"];
  const q2 = ["July", "August", "September"];
  const q3 = ["October", "November", "December"];
  if (q1.includes(month)) return "Q1";
  if (q2.includes(month)) return "Q2";
  if (q3.includes(month)) return "Q3";
  return "Q4";
}

// ─── Component Props ─────────────────────────────────────────────────────────

interface ActualsDashboardProps {
  apiPrefix: string;        // "/admin" for admin, "/team" for leadership
  fiscalYear: string;       // e.g. "FY26"
  ccsByBU?: Record<string, { cost_center_number: string; cost_center_name: string }[]>;
}

const ActualsDashboard: React.FC<ActualsDashboardProps> = ({ apiPrefix, fiscalYear, ccsByBU: ccsByBUProp }) => {
  // ── State ──────────────────────────────────────────────────────────────────
  const [dashMonth, setDashMonth] = useState<string>("");
  const [dashAvailableMonths, setDashAvailableMonths] = useState<string[]>([]);
  const [dashBU, setDashBU] = useState<string>("");
  const [dashSelectedCCs, setDashSelectedCCs] = useState<string[]>([]);
  const [dashBusinessUnits, setDashBusinessUnits] = useState<string[]>([]);
  const [dashBUSummary, setDashBUSummary] = useState<BUSummaryRow[]>([]);
  const [dashData, setDashData] = useState<CategoryKPI[]>([]);
  const [dashLoading, setDashLoading] = useState(false);
  const [dashMonthsLoading, setDashMonthsLoading] = useState(false);
  const [drillCC, setDrillCC] = useState<string | null>(null);
  const [drillCategory, setDrillCategory] = useState<string | null>(null);
  const [drillData, setDrillData] = useState<AcctRow[] | null>(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [dashMonthlyTrend, setDashMonthlyTrend] = useState<MonthlyTrendPoint[] | null>(null);
  const [dashAnnualBudgetTotal, setDashAnnualBudgetTotal] = useState<number>(0);
  const [dashCCSummary, setDashCCSummary] = useState<{cost_center: string; cost_center_name: string; business_unit: string; month_budget: number; month_actual: number; ytd_budget: number; ytd_actual: number; month_variance: number; ytd_variance: number; qtr_budget: number; categories: CCCatBreakdown[]}[]>([]);
  const [expandedCCs, setExpandedCCs] = useState<Set<string>>(new Set());

  // CCs by BU — use prop if provided, otherwise fetch
  const [ccsByBULocal, setCCsByBULocal] = useState<Record<string, { cost_center_number: string; cost_center_name: string }[]>>({});
  const ccsByBU = ccsByBUProp || ccsByBULocal;

  useEffect(() => {
    if (!ccsByBUProp) {
      api.get(`${apiPrefix}/cost-centers-by-bu`, { params: { fy: fiscalYear } })
        .then(res => setCCsByBULocal(res.data.by_bu || {}))
        .catch(() => {});
    }
  }, [apiPrefix, ccsByBUProp, fiscalYear]);

  // ── Data loading ───────────────────────────────────────────────────────────
  const doLoadDashboard = useCallback((month: string, bu: string, selectedCCs: string[] = [], ytd = false) => {
    if (!month) return;
    setDashLoading(true);
    setDashData([]);
    setDashBUSummary([]);
    setDrillData(null);
    setDrillCC(null);
    setDrillCategory(null);
    setExpandedCCs(new Set());
    const params = new URLSearchParams({ month });
    if (bu) params.set("bu", bu);
    if (selectedCCs.length > 0) params.set("cc", selectedCCs.join(","));
    if (fiscalYear) params.set("fiscalYear", fiscalYear);
    if (ytd) params.set("ytd", "1");
    api.get(`${apiPrefix}/bu-dashboard?${params}`)
      .then(res => {
        setDashData(res.data.categories || []);
        setDashBusinessUnits(res.data.business_units || []);
        setDashBUSummary(res.data.bu_summary || []);
        setDashCCSummary(res.data.cc_summary || []);
        setDashLoading(false);
      })
      .catch(() => setDashLoading(false));
    // Fetch monthly trend in parallel for projection KPIs
    const trendParams = new URLSearchParams();
    if (bu) trendParams.set("bu", bu);
    if (selectedCCs.length > 0) trendParams.set("cc", selectedCCs.join(","));
    if (fiscalYear) trendParams.set("fiscalYear", fiscalYear);
    api.get(`${apiPrefix}/bu-dashboard/monthly-trend?${trendParams}`)
      .then(res => {
        setDashMonthlyTrend(res.data.months || []);
        setDashAnnualBudgetTotal(res.data.annual_budget_total || 0);
      })
      .catch(() => { setDashMonthlyTrend(null); setDashAnnualBudgetTotal(0); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fiscalYear, apiPrefix]);

  const doLoadAvailableMonths = useCallback((bu: string) => {
    setDashMonthsLoading(true);
    const params = new URLSearchParams();
    if (bu) params.set("bu", bu);
    if (fiscalYear) params.set("fy", fiscalYear);
    api.get(`${apiPrefix}/bu-dashboard/available-months?${params}`)
      .then(res => {
        const months: string[] = res.data.months || [];
        setDashAvailableMonths(months);
        const latest = months[months.length - 1] || "";
        setDashMonth(latest);
        setDashMonthsLoading(false);
        if (latest) doLoadDashboard(latest, bu);
      })
      .catch(() => setDashMonthsLoading(false));
  }, [doLoadDashboard, fiscalYear, apiPrefix]);

  // Auto-load on mount
  useEffect(() => {
    if (dashAvailableMonths.length === 0 && !dashMonthsLoading) {
      doLoadAvailableMonths(dashBU);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-select BU when there is exactly one
  useEffect(() => {
    if (dashBusinessUnits.length === 1 && !dashBU) {
      setDashBU(dashBusinessUnits[0]);
    }
  }, [dashBusinessUnits, dashBU]);

  // ── Projected Year-End ─────────────────────────────────────────────────────
  const dashProjection = useMemo(() => {
    if (!dashMonthlyTrend || dashMonthlyTrend.length === 0) return null;
    const actMonths = dashMonthlyTrend.filter(p => p.hasActuals);
    const ytdActualTotal = actMonths.reduce((s, p) => s + (p.actual || 0), 0);
    const n = actMonths.length;
    const remaining = dashMonthlyTrend.filter(p => !p.hasActuals);
    const remainingBudgetTotal = remaining.reduce((s, p) => s + (p.budget || 0), 0);
    const forecastTotal = dashMonthlyTrend.reduce((s, p) => s + (p.budget || 0), 0);
    const projectedYearEnd = n === 0 ? forecastTotal : ytdActualTotal + remainingBudgetTotal;

    return {
      forecastTotal,
      ytdActualTotal,
      annualBudgetTotal: dashAnnualBudgetTotal,
      projectedYearEnd: Math.round(projectedYearEnd),
      projectionVariance: Math.round(projectedYearEnd - forecastTotal),
      budgetVariance: Math.round(projectedYearEnd - forecastTotal),
      actualMonths: n,
      remainingMonths: remaining.length,
    };
  }, [dashMonthlyTrend, dashAnnualBudgetTotal]);

  const doLoadDrilldown = (category: string, cc?: string) => {
    if (drillCategory === category && ((!cc && !drillCC) || (cc && drillCC === cc))) {
      setDrillData(null);
      setDrillCC(null);
      setDrillCategory(null);
      return;
    }
    setDrillCategory(category);
    setDrillCC(cc || null);
    setDrillLoading(true);
    setDrillData([]);
    const effectiveMon = dashMonth === "YTD" ? dashAvailableMonths[dashAvailableMonths.length - 1] : dashMonth;
    const params = new URLSearchParams({ category, month: effectiveMon });
    if (cc) params.set("cc", cc);
    if (dashMonth === "YTD") params.set("ytd", "1");
    if (fiscalYear) params.set("fiscalYear", fiscalYear);
    api.get(`${apiPrefix}/bu-dashboard/cc-detail?${params}`)
      .then(res => { setDrillData(res.data.accounts || []); setDrillLoading(false); })
      .catch(() => setDrillLoading(false));
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <span className="material-icons">bar_chart</span>
        <span className="admin-section-title">Actuals Dashboard</span>
        <span className="admin-section-subtitle">Budget vs Actuals · month &amp; YTD</span>
        <div style={{ marginLeft: "auto", display: "flex", gap: "8px", alignItems: "center" }}>
          {/* BU filter */}
          {dashBusinessUnits.length > 0 && (
            <select
              className="admin-select"
              value={dashBU}
              onChange={e => {
                const bu = e.target.value;
                setDashBU(bu);
                setDashSelectedCCs([]);
                setDashAvailableMonths([]);
                setDashMonth("");
                doLoadAvailableMonths(bu);
              }}
            >
              <option value="">All Business Units</option>
              {dashBusinessUnits.map(bu => <option key={bu} value={bu}>{bu}</option>)}
            </select>
          )}
          {/* Month filter */}
          {dashAvailableMonths.length > 0 && (
            <select
              className="admin-select"
              value={dashMonth}
              onChange={e => {
                const val = e.target.value;
                setDashMonth(val);
                if (val === "YTD") {
                  const latest = dashAvailableMonths[dashAvailableMonths.length - 1];
                  if (latest) doLoadDashboard(latest, dashBU, dashSelectedCCs, true);
                } else {
                  doLoadDashboard(val, dashBU, dashSelectedCCs, false);
                }
              }}
            >
              {dashAvailableMonths.map(m => <option key={m} value={m}>{m}</option>)}
              <option value="YTD">YTD</option>
            </select>
          )}
          <button className="admin-btn admin-btn--ghost" onClick={() => {
            const eff = dashMonth === "YTD" ? dashAvailableMonths[dashAvailableMonths.length - 1] : dashMonth;
            doLoadDashboard(eff, dashBU, dashSelectedCCs, dashMonth === "YTD");
          }}>
            <span className="material-icons">refresh</span>
            Refresh
          </button>
        </div>
      </div>

      {/* ── CC filter pills ── */}
      {dashBU && ccsByBU[dashBU] && ccsByBU[dashBU].length > 0 && (
        <div className="admin-cc-filter-bar">
          <span className="admin-cc-filter-label">Cost Centers:</span>
          <button
            className={`admin-cc-pill${dashSelectedCCs.length === 0 ? " admin-cc-pill--active" : ""}`}
            onClick={() => { setDashSelectedCCs([]); if (dashMonth) doLoadDashboard(dashMonth, dashBU, []); }}
          >All</button>
          {ccsByBU[dashBU].map(cc => (
            <button
              key={cc.cost_center_number}
              className={`admin-cc-pill${dashSelectedCCs.includes(cc.cost_center_number) ? " admin-cc-pill--active" : ""}`}
              title={cc.cost_center_name}
              onClick={() => {
                const next = dashSelectedCCs.includes(cc.cost_center_number)
                  ? dashSelectedCCs.filter(c => c !== cc.cost_center_number)
                  : [...dashSelectedCCs, cc.cost_center_number];
                setDashSelectedCCs(next);
                if (dashMonth) doLoadDashboard(dashMonth, dashBU, next);
              }}
            >{cc.cost_center_number}</button>
          ))}
        </div>
      )}

      {/* ── Budget Reference Dimension Banner ── */}
      {dashMonth && !dashMonthsLoading && !dashLoading && (() => {
        const annualDim = fiscalYear ? `Annual Budget ${fiscalYear.toUpperCase()}` : "Annual Budget";
        const PERIODS = [
          { dim: annualDim,            months: ["April","May","June"],                            range: "Apr–Jun" },
          { dim: "July Forecast",      months: ["July","August","September"],                     range: "Jul–Sep" },
          { dim: "October Forecast",   months: ["October","November","December"],                 range: "Oct–Dec" },
          { dim: "January Forecast",   months: ["January","February","March"],                    range: "Jan–Mar" },
        ];
        if (dashMonth === "YTD") {
          const latest = dashAvailableMonths[dashAvailableMonths.length - 1];
          const FISCAL = ["April","May","June","July","August","September","October","November","December","January","February","March"];
          const latestIdx = FISCAL.indexOf(latest);
          const usedPeriods = PERIODS.filter(p => p.months.some(m => FISCAL.indexOf(m) <= latestIdx));
          return (
            <div className="admin-dash-ref-bar">
              <span className="material-icons" style={{ fontSize: 14, color: "#6b7280" }}>info</span>
              <span style={{ color: "#6b7280", fontWeight: 600 }}>Budget Reference (YTD through {latest}):</span>
              {usedPeriods.map(p => (
                <span key={p.dim} className="admin-dash-ref-chip">{p.dim} <span style={{ fontWeight: 400, opacity: 0.7 }}>({p.range})</span></span>
              ))}
            </div>
          );
        }
        const refPeriod = PERIODS.find(p => p.months.includes(dashMonth));
        if (!refPeriod) return null;
        return (
          <div className="admin-dash-ref-bar">
            <span className="material-icons" style={{ fontSize: 14, color: "#6b7280" }}>info</span>
            <span style={{ color: "#6b7280", fontWeight: 600 }}>Budget Reference for {dashMonth}:</span>
            <span className="admin-dash-ref-chip">{refPeriod.dim}</span>
          </div>
        );
      })()}

      {dashMonthsLoading || dashLoading ? (
        <div style={{ padding: "24px", color: "#9ca3af", fontSize: "13px" }}>Loading…</div>
      ) : !dashMonth ? (
        <div style={{ padding: "24px", color: "#9ca3af", fontSize: "13px" }}>
          No actuals uploaded yet. Upload actuals to see this dashboard.
        </div>
      ) : dashData.length === 0 ? (
        <div style={{ padding: "24px", color: "#9ca3af", fontSize: "13px" }}>
          No data for <strong>{dashMonth}</strong>. Upload a budget to see dashboard data.
        </div>
      ) : (
        <>
          {/* ── KPI Summary Strip ── */}
          {dashProjection && (() => {
            const isYTD = dashMonth === "YTD";
            const periodLabel = isYTD ? "YTD" : dashMonth;
            const periodBudget = dashData.filter(cat => !EXCLUDED_CATS.has(cat.category))
              .reduce((s, cat) => s + (isYTD ? cat.ytd_budget : cat.month_budget), 0);
            const periodActual = dashData.filter(cat => !EXCLUDED_CATS.has(cat.category))
              .reduce((s, cat) => s + (isYTD ? cat.ytd_actual : cat.month_actual), 0);
            const periodVariance = periodActual - periodBudget;
            const periodIsOver = periodVariance > 0;
            return (
            <div className="dash-kpi-strip">
              <div className="dash-kpi-card">
                <div className="dash-kpi-label">{periodLabel} Actual</div>
                <div className="dash-kpi-value">{fmtMoney(periodActual)}</div>
                <div className="dash-kpi-sub">
                  {isYTD ? `Apr \u2013 ${dashAvailableMonths[dashAvailableMonths.length - 1] || "\u2014"}` : `${dashMonth} actual spend`}
                </div>
              </div>
              <div className="dash-kpi-card">
                <div className="dash-kpi-label">
                  <span className="material-icons">account_balance</span>
                  {periodLabel} Budget
                </div>
                <div className="dash-kpi-value">{fmtMoney(periodBudget)}</div>
                <div className="dash-kpi-sub">
                  {isYTD ? `Budget Apr \u2013 ${dashAvailableMonths[dashAvailableMonths.length - 1] || "\u2014"}` : `${dashMonth} budget`}
                </div>
              </div>
              <div className={`dash-kpi-card ${periodIsOver ? "dash-kpi-card--warn" : "dash-kpi-card--accent"}`}>
                <div className={`dash-kpi-label ${periodIsOver ? "dash-kpi-label--red" : "dash-kpi-label--green"}`}>
                  <span className="material-icons">{periodIsOver ? "warning" : "check_circle"}</span>
                  {periodIsOver ? "Over Budget" : "Under Budget"}
                </div>
                <div className={`dash-kpi-value ${periodIsOver ? "dash-kpi-value--red" : "dash-kpi-value--green"}`}>
                  {fmtVar(periodVariance)}
                </div>
                <div className={`dash-kpi-sub ${periodIsOver ? "dash-kpi-sub--red" : "dash-kpi-sub--green"}`}>
                  {periodLabel} actual vs budget
                </div>
              </div>
              <div className="dash-kpi-card">
                <div className="dash-kpi-label">
                  <span className="material-icons">compare_arrows</span>
                  MPY vs Budget
                </div>
                <div className="dash-kpi-vs">
                  <span className="dash-kpi-value">{fmtMoney(dashProjection.projectedYearEnd)}</span>
                  <span className="dash-kpi-vs-sep">vs</span>
                  <span className="dash-kpi-vs-secondary">{fmtMoney(dashProjection.annualBudgetTotal)}</span>
                </div>
                <div className="dash-kpi-sub">Projected vs Full-Year Budget</div>
              </div>
            </div>
            );
          })()}

          {/* ── BU Summary table ── */}
          {!dashBU && dashBUSummary.length > 1 && (() => {
            const isYTD = dashMonth === "YTD";
            const periodLabel = isYTD ? "YTD" : dashMonth;
            const qLabel = isYTD ? "YTD" : getFiscalQuarter(dashMonth);
            return (
            <div className="dash-cat-table-wrap">
              <div className="bu-summary-header">
                <div className="bu-summary-header-left">
                  <span className="material-icons">business</span>
                  <span className="bu-summary-title">Business Unit Summary</span>
                </div>
                <span className="bu-summary-period">{periodLabel} · {dashBUSummary.length} business units</span>
              </div>
              <table className="dash-cat-table dash-var-table">
                <thead>
                  <tr>
                    <th className="dash-cat-th-left dash-var-col--left">Business Unit</th>
                    <th className="dash-var-col--num">{periodLabel} Budget</th>
                    <th className="dash-var-col--num">{periodLabel} Actuals</th>
                    <th className="dash-var-col--num">{periodLabel} Variance</th>
                    <th className="dash-var-col--num">YTD</th>
                    <th className="dash-var-col--sm">{qLabel} Util</th>
                    <th className="dash-var-col--num">YTG</th>
                    <th className="dash-var-col--bar">Category Breakdown</th>
                  </tr>
                </thead>
                <tbody>
                  {dashBUSummary.map(row => {
                    const dBudget   = isYTD ? row.ytd_budget   : row.month_budget;
                    const dActual   = isYTD ? row.ytd_actual   : row.month_actual;
                    const dVariance = isYTD ? row.ytd_variance : row.month_variance;
                    const utilBase  = isYTD ? row.ytd_budget : (row.qtr_budget || row.ytd_budget);
                    const utilPct   = utilBase > 0 ? (row.ytd_actual / utilBase) * 100 : 0;
                    const ytg       = (row.annual_budget || 0) - row.ytd_actual;
                    const varClass  = dVariance <= 0 ? "dash-fav" : "dash-unfav";

                    const catVars = (row.categories || [])
                      .filter(c => !EXCLUDED_CATS.has(c.category))
                      .map(c => ({
                        category: c.category,
                        variance: isYTD ? c.ytd_variance : c.month_variance,
                      }))
                      .filter(c => c.variance !== 0)
                      .sort((a, b) => Math.abs(b.variance) - Math.abs(a.variance));
                    const totalAbsVar = catVars.reduce((s, c) => s + Math.abs(c.variance), 0) || 1;

                    return (
                    <tr
                      key={row.business_unit}
                      className="dash-cat-tr"
                      onClick={() => { setDashBU(row.business_unit); setDashSelectedCCs([]); doLoadDashboard(dashMonth, row.business_unit, []); }}
                      title={`Click to drill into ${row.business_unit}`}
                    >
                      <td className="dash-cat-td-name" style={{ fontWeight: 600 }}>{row.business_unit}</td>
                      <td className="dash-cat-td-num">{fmtMoney(dBudget)}</td>
                      <td className="dash-cat-td-num">{fmtMoney(dActual)}</td>
                      <td className={`dash-cat-td-num ${varClass}`}>
                        {fmtVar(dVariance)}
                      </td>
                      <td className="dash-cat-td-num">{fmtMoney(row.ytd_actual)}</td>
                      <td className={`dash-cat-td-num dash-cat-td-util ${utilPct > 100 ? "dash-unfav" : ""}`}>
                        {utilPct > 0 ? `${Math.round(utilPct)}%` : "\u2014"}
                      </td>
                      <td className={`dash-cat-td-num ${ytg < 0 ? "dash-unfav" : "dash-fav"}`}>
                        {fmtYTG(ytg)}
                      </td>
                      <td className="ccx-td-breakdown">
                        {catVars.length > 0 ? (
                          <div className="ccx-bar-stack">
                            {catVars.map(cv => {
                              const pct = Math.abs(cv.variance) / totalAbsVar * 100;
                              if (pct < 3) return null;
                              return (
                                <div
                                  key={cv.category}
                                  className="ccx-bar-seg"
                                  style={{ width: `${pct}%`, background: CAT_COLOR(cv.category), opacity: cv.variance > 0 ? 1 : 0.4 }}
                                  title={`${cv.category}: ${fmtVar(cv.variance)}`}
                                />
                              );
                            })}
                          </div>
                        ) : (
                          <span style={{ color: "#9ca3af", fontSize: "11px" }}>{"\u2014"}</span>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            );
          })()}

          {/* ── Category Variance table ── */}
          {(() => {
            const isYTD = dashMonth === "YTD";
            const cats = dashData.filter(cat => !EXCLUDED_CATS.has(cat.category));
            return (
              <div className="dash-cat-table-wrap">
                <div className="ccx-header">
                  <span className="material-icons" style={{ fontSize: "16px", color: "#6366f1" }}>pie_chart</span>
                  <span className="ccx-title">Category Variance</span>
                  <span className="ccx-subtitle">{isYTD ? "YTD" : dashMonth} · click a row to filter cost centers</span>
                  <span className="ccx-count">{cats.length} categories</span>
                </div>
                <table className="dash-cat-table dash-var-table">
                  <thead>
                    <tr>
                      <th className="dash-cat-th-left dash-var-col--left">Category</th>
                      <th className="dash-var-col--num">{isYTD ? "YTD" : dashMonth} Budget</th>
                      <th className="dash-var-col--num">{isYTD ? "YTD" : dashMonth} Actuals</th>
                      <th className="dash-var-col--num">{isYTD ? "YTD" : dashMonth} Variance</th>
                      <th className="dash-var-col--num">YTD</th>
                      <th className="dash-var-col--sm">{isYTD ? "YTD" : getFiscalQuarter(dashMonth)} Util</th>
                      <th className="dash-var-col--num">YTG</th>
                      <th className="dash-var-col--bar">CC Breakdown</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cats.map(cat => {
                      const color     = CAT_COLOR(cat.category);
                      const dBudget   = isYTD ? cat.ytd_budget   : cat.month_budget;
                      const dActual   = isYTD ? cat.ytd_actual   : cat.month_actual;
                      const dVariance = isYTD ? cat.ytd_variance : cat.month_variance;
                      const isActive  = drillCategory === cat.category && !drillCC;
                      const varClass  = dVariance <= 0 ? "dash-fav" : "dash-unfav";
                      const utilBase = isYTD ? cat.ytd_budget : (cat.qtr_budget || cat.ytd_budget);
                      const catUtilPct = utilBase > 0 ? (cat.ytd_actual / utilBase) * 100 : 0;
                      const catYTG = (cat.annual_budget || 0) - cat.ytd_actual;

                      const allCCs = [...(cat.top_over || []), ...(cat.top_under || [])];
                      const ccVars = allCCs.map(c => ({
                        cc: c.cost_center, name: c.cost_center_name,
                        variance: isYTD ? c.ytd_variance : c.month_variance,
                      })).filter(c => c.variance !== 0)
                        .sort((a, b) => Math.abs(b.variance) - Math.abs(a.variance));
                      const totalAbsVar = ccVars.reduce((s, c) => s + Math.abs(c.variance), 0) || 1;

                      return (
                        <React.Fragment key={cat.category}>
                          <tr
                            className={`dash-cat-tr${isActive ? " dash-cat-tr--active" : ""}`}
                            onClick={() => doLoadDrilldown(cat.category)}
                            title={`Click to see ${cat.category} accounts`}
                          >
                            <td className="dash-cat-td-name">
                              <span className="material-icons dash-cat-expand-icon">{isActive ? "expand_less" : "expand_more"}</span>
                              <span className="dash-cat-dot" style={{ background: color }} />
                              {cat.category}
                            </td>
                            <td className="dash-cat-td-num">{fmtMoney(dBudget)}</td>
                            <td className="dash-cat-td-num">{fmtMoney(dActual)}</td>
                            <td className={`dash-cat-td-num ${varClass}`}>
                              {fmtVar(dVariance)}
                            </td>
                            <td className="dash-cat-td-num">{fmtMoney(cat.ytd_actual)}</td>
                            <td className={`dash-cat-td-num dash-cat-td-util ${catUtilPct > 100 ? "dash-unfav" : ""}`}>
                              {catUtilPct > 0 ? `${Math.round(catUtilPct)}%` : "\u2014"}
                            </td>
                            <td className={`dash-cat-td-num ${catYTG < 0 ? "dash-unfav" : "dash-fav"}`}>
                              {fmtYTG(catYTG)}
                            </td>
                            <td className="ccx-td-breakdown">
                              {ccVars.length > 0 ? (
                                <div className="ccx-bar-stack">
                                  {ccVars.map(cv => {
                                    const pct = Math.abs(cv.variance) / totalAbsVar * 100;
                                    if (pct < 3) return null;
                                    return (
                                      <div
                                        key={cv.cc}
                                        className="ccx-bar-seg"
                                        style={{ width: `${pct}%`, background: color, opacity: cv.variance > 0 ? 1 : 0.4 }}
                                        title={`CC ${cv.cc} ${cv.name}: ${fmtVar(cv.variance)}`}
                                      />
                                    );
                                  })}
                                </div>
                              ) : (
                                <span style={{ color: "#9ca3af", fontSize: "11px" }}>—</span>
                              )}
                            </td>
                          </tr>

                          {/* Expanded: account breakdown for this category */}
                          {isActive && drillData !== null && (
                            <tr className="ccx-expand-row">
                              <td colSpan={8}>
                                <div className="ccx-expand-inner">
                                  <div className="ccx-drill-head">
                                    <span className="material-icons">receipt_long</span>
                                    <span>Accounts: {cat.category}</span>
                                    <button
                                      className="admin-btn admin-btn--ghost"
                                      style={{ marginLeft: "auto", padding: "2px 8px" }}
                                      onClick={(e) => { e.stopPropagation(); setDrillData(null); setDrillCC(null); setDrillCategory(null); }}
                                    >
                                      <span className="material-icons">close</span>
                                    </button>
                                  </div>
                                  {drillLoading ? (
                                    <div style={{ padding: "12px", color: "#9ca3af", fontSize: "13px" }}>Loading…</div>
                                  ) : drillData.length === 0 ? (
                                    <div style={{ padding: "12px", color: "#9ca3af", fontSize: "13px" }}>No account data found.</div>
                                  ) : (
                                    <table className="ccx-acct-table dash-var-table">
                                      <thead>
                                        <tr>
                                          <th className="ccx-th-left dash-var-col--left">Account</th>
                                          <th className="num dash-var-col--num">{isYTD ? "YTD" : dashMonth} Budget</th>
                                          <th className="num dash-var-col--num">{isYTD ? "YTD" : dashMonth} Actuals</th>
                                          <th className="num dash-var-col--num">{isYTD ? "YTD" : dashMonth} Variance</th>
                                          <th className="num dash-var-col--num">YTD</th>
                                          <th className="num dash-var-col--sm">{isYTD ? "YTD" : getFiscalQuarter(dashMonth)} Util</th>
                                          <th className="num dash-var-col--num">YTG</th>
                                          <th className="dash-var-col--bar"></th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {drillData.map(r => {
                                          const num  = r.account.includes(" - ") ? r.account.split(" - ")[0] : "";
                                          const name = r.account.includes(" - ") ? r.account.split(" - ").slice(1).join(" - ") : r.account;
                                          const acctUtilPct = r.ytd_budget > 0 ? (r.ytd_actual / r.ytd_budget) * 100 : 0;
                                          const acctYTG = (r.annual_budget || 0) - r.ytd_actual;
                                          return (
                                            <tr key={r.account}>
                                              <td className="ccx-td-acct-name"><span className="ccx-td-acct-num">{num}</span> {name}</td>
                                              <td className="num">{fmtMoney(r.month_budget)}</td>
                                              <td className="num">{fmtMoney(r.month_actual)}</td>
                                              <td className="num">
                                                <span className={r.month_variance > 0 ? "admin-dash-var-over" : r.month_variance < 0 ? "admin-dash-var-under" : ""}>
                                                  {fmtVar(r.month_variance)}
                                                </span>
                                              </td>
                                              <td className="num">{fmtMoney(r.ytd_actual)}</td>
                                              <td className={`num ${acctUtilPct > 100 ? "dash-unfav" : ""}`}>
                                                {acctUtilPct > 0 ? `${Math.round(acctUtilPct)}%` : "\u2014"}
                                              </td>
                                              <td className={`num ${acctYTG < 0 ? "dash-unfav" : "dash-fav"}`}>
                                                {fmtYTG(acctYTG)}
                                              </td>
                                              <td></td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}

          {/* ── CC Variance Explorer ── */}
          {dashCCSummary.length > 0 && (() => {
            const isYTD = dashMonth === "YTD";
            const allRows = dashCCSummary.filter(c => {
              const v = isYTD ? c.ytd_variance : c.month_variance;
              return v !== 0;
            });
            if (allRows.length === 0) return null;
            const rows = drillCategory
              ? allRows.filter(c => (c.categories || []).some(cat => cat.category === drillCategory))
              : allRows;
            const periodLabel = isYTD ? "YTD" : dashMonth;

            return (
              <div className="ccx-card">
                <div className="ccx-header">
                  <span className="material-icons" style={{ fontSize: "16px", color: "#6366f1" }}>account_tree</span>
                  <span className="ccx-title">Cost Center Variance</span>
                  <span className="ccx-subtitle">{periodLabel} · click a row to see category breakdown</span>
                  {drillCategory && (
                    <span className="ccx-filter-pill">
                      {drillCategory}
                      <span className="material-icons" style={{ fontSize: "14px", cursor: "pointer" }}
                        onClick={(e) => { e.stopPropagation(); setDrillCategory(null); setDrillCC(null); setDrillData(null); }}>close</span>
                    </span>
                  )}
                  <span className="ccx-count">{rows.length} cost centers</span>
                </div>
                <table className="ccx-table dash-var-table">
                  <thead className="ccx-thead--bold">
                    <tr>
                      <th className="ccx-th-left dash-var-col--cc">CC</th>
                      <th className="ccx-th-left dash-var-col--ccname">Name</th>
                      <th className="ccx-th-num dash-var-col--num">{periodLabel} Budget</th>
                      <th className="ccx-th-num dash-var-col--num">{periodLabel} Actual</th>
                      <th className="ccx-th-num dash-var-col--num">{periodLabel} Variance</th>
                      <th className="ccx-th-num dash-var-col--num">YTD</th>
                      <th className="ccx-th-num dash-var-col--sm">{isYTD ? "YTD" : getFiscalQuarter(dashMonth)} Util</th>
                      <th className="ccx-th-num dash-var-col--num">YTG</th>
                      <th className="ccx-th-num dash-var-col--bar">Category Breakdown</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(cc => {
                      let budget: number, actual: number, variance: number;
                      let utilPct: number, ytdActual: number, ytdBudget: number;
                      let qtrBudget: number;
                      if (drillCategory) {
                        const catMatch = (cc.categories || []).find(cat => cat.category === drillCategory);
                        budget    = catMatch ? (isYTD ? catMatch.ytd_budget   : catMatch.month_budget)   : 0;
                        actual    = catMatch ? (isYTD ? catMatch.ytd_actual   : catMatch.month_actual)   : 0;
                        variance  = actual - budget;
                        ytdActual = catMatch ? catMatch.ytd_actual : 0;
                        ytdBudget = catMatch ? catMatch.ytd_budget : 0;
                        qtrBudget = catMatch ? (catMatch.qtr_budget || catMatch.ytd_budget) : 0;
                      } else {
                        budget    = isYTD ? cc.ytd_budget   : cc.month_budget;
                        actual    = isYTD ? cc.ytd_actual   : cc.month_actual;
                        variance  = isYTD ? cc.ytd_variance : cc.month_variance;
                        ytdActual = cc.ytd_actual;
                        ytdBudget = cc.ytd_budget;
                        qtrBudget = cc.qtr_budget || cc.ytd_budget;
                      }
                      const utilDenom = isYTD ? ytdBudget : qtrBudget;
                      utilPct   = utilDenom > 0 ? (ytdActual / utilDenom) * 100 : 0;
                      const ytg = (cc.annual_budget || 0) - ytdActual;
                      const isOver   = variance > 0;
                      const isExpanded = expandedCCs.has(cc.cost_center);

                      const catVars = (cc.categories || [])
                        .map(cat => ({
                          category: cat.category,
                          variance: isYTD ? cat.ytd_variance : cat.month_variance,
                        }))
                        .filter(c => c.variance !== 0);
                      const totalAbsVar = catVars.reduce((s, c) => s + Math.abs(c.variance), 0) || 1;

                      return (
                        <React.Fragment key={cc.cost_center}>
                          <tr
                            className={`ccx-row${isExpanded ? " ccx-row--expanded" : ""}`}
                            onClick={() => {
                              setExpandedCCs(prev => {
                                const next = new Set(prev);
                                if (next.has(cc.cost_center)) next.delete(cc.cost_center);
                                else next.add(cc.cost_center);
                                return next;
                              });
                            }}
                          >
                            <td className="ccx-td-cc">{cc.cost_center}</td>
                            <td className="ccx-td-name">{cc.cost_center_name}</td>
                            <td className="ccx-td-num">{fmtMoney(budget)}</td>
                            <td className="ccx-td-num">{fmtMoney(actual)}</td>
                            <td className={`ccx-td-num ccx-td-var ${isOver ? "dash-unfav" : "dash-fav"}`}>
                              {fmtVar(variance)}
                            </td>
                            <td className="ccx-td-num">{fmtMoney(ytdActual)}</td>
                            <td className={`ccx-td-num ${utilPct > 100 ? "dash-unfav" : ""}`}>
                              {utilPct > 0 ? `${Math.round(utilPct)}%` : "\u2014"}
                            </td>
                            <td className={`ccx-td-num ${ytg < 0 ? "dash-unfav" : "dash-fav"}`}>
                              {fmtYTG(ytg)}
                            </td>
                            <td className="ccx-td-breakdown">
                              <div className="ccx-bar-stack">
                                {catVars.map(cv => {
                                  const pct = Math.abs(cv.variance) / totalAbsVar * 100;
                                  if (pct < 2) return null;
                                  return (
                                    <div
                                      key={cv.category}
                                      className="ccx-bar-seg"
                                      style={{ width: `${pct}%`, background: CAT_COLOR(cv.category), opacity: cv.variance > 0 ? 1 : 0.45 }}
                                      title={`${cv.category}: ${fmtVar(cv.variance)}`}
                                    />
                                  );
                                })}
                              </div>
                            </td>
                          </tr>

                          {/* Expanded: category breakdown */}
                          {isExpanded && (() => {
                            const ccCats = (cc.categories || []).filter(cat => !EXCLUDED_CATS.has(cat.category));
                            return (
                            <tr className="ccx-expand-row">
                              <td colSpan={9}>
                                <div className="ccx-expand-inner">
                                  {ccCats.length === 0 ? (
                                    <div style={{ padding: "12px", color: "#9ca3af", fontSize: "12px" }}>No category data.</div>
                                  ) : (
                                  <table className="ccx-cat-table">
                                    <thead>
                                      <tr>
                                        <th className="ccx-th-left">Category</th>
                                        <th className="ccx-th-num">Budget</th>
                                        <th className="ccx-th-num">Actual</th>
                                        <th className="ccx-th-num">Variance</th>
                                        <th className="ccx-th-num" style={{ width: "80px" }}>Util %</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {ccCats.map(cat => {
                                        const cBudget   = isYTD ? cat.ytd_budget   : cat.month_budget;
                                        const cActual   = isYTD ? cat.ytd_actual   : cat.month_actual;
                                        const cVariance = isYTD ? cat.ytd_variance  : cat.month_variance;
                                        const cUtilBase = isYTD ? cat.ytd_budget : (cat.qtr_budget || cat.ytd_budget);
                                        const cUtil     = cUtilBase > 0 ? (cat.ytd_actual / cUtilBase) * 100 : 0;
                                        const catColor  = CAT_COLOR(cat.category);
                                        const isActiveDrill = drillCC === cc.cost_center && drillCategory === cat.category;
                                        return (
                                          <React.Fragment key={cat.category}>
                                            <tr
                                              className={`ccx-cat-row${isActiveDrill ? " ccx-cat-row--active" : ""}`}
                                              onClick={(e) => { e.stopPropagation(); doLoadDrilldown(cat.category, cc.cost_center); }}
                                            >
                                              <td className="ccx-cat-td-name">
                                                <span className="dash-cat-dot" style={{ background: catColor }} />
                                                {cat.category}
                                                <span className="material-icons ccx-cat-drill-icon">chevron_right</span>
                                              </td>
                                              <td className="ccx-td-num">{fmtMoney(cBudget)}</td>
                                              <td className="ccx-td-num">{fmtMoney(cActual)}</td>
                                              <td className={`ccx-td-num ${cVariance > 0 ? "dash-unfav" : cVariance < 0 ? "dash-fav" : ""}`}>
                                                {fmtVar(cVariance)}
                                              </td>
                                              <td className={`ccx-td-num ${cUtil > 100 ? "dash-unfav" : ""}`}>
                                                {Math.round(cUtil)}%
                                              </td>
                                            </tr>

                                            {/* Account-level drilldown */}
                                            {isActiveDrill && drillData !== null && (
                                              <tr className="ccx-drill-row">
                                                <td colSpan={5}>
                                                  <div className="ccx-drill-inner">
                                                    <div className="ccx-drill-head">
                                                      <span className="material-icons">receipt_long</span>
                                                      <span>Accounts: {cat.category} — CC {cc.cost_center}</span>
                                                      <button
                                                        className="admin-btn admin-btn--ghost"
                                                        style={{ marginLeft: "auto", padding: "2px 8px" }}
                                                        onClick={(e) => { e.stopPropagation(); setDrillData(null); setDrillCC(null); setDrillCategory(null); }}
                                                      >
                                                        <span className="material-icons">close</span>
                                                      </button>
                                                    </div>
                                                    {drillLoading ? (
                                                      <div style={{ padding: "12px", color: "#9ca3af", fontSize: "13px" }}>Loading…</div>
                                                    ) : drillData.length === 0 ? (
                                                      <div style={{ padding: "12px", color: "#9ca3af", fontSize: "13px" }}>No account data found.</div>
                                                    ) : (
                                                      <table className="ccx-acct-table">
                                                        <thead>
                                                          <tr>
                                                            <th className="ccx-th-left">Account</th>
                                                            <th className="num">{periodLabel} Budget</th>
                                                            <th className="num">{periodLabel} Actuals</th>
                                                            <th className="num">{periodLabel} Variance</th>
                                                            <th className="num">YTD</th>
                                                          </tr>
                                                        </thead>
                                                        <tbody>
                                                          {drillData.map(r => {
                                                            const num  = r.account.includes(" - ") ? r.account.split(" - ")[0] : "";
                                                            const name = r.account.includes(" - ") ? r.account.split(" - ").slice(1).join(" - ") : r.account;
                                                            return (
                                                              <tr key={r.account}>
                                                                <td className="ccx-td-acct-name"><span className="ccx-td-acct-num">{num}</span> {name}</td>
                                                                <td className="num">{fmtMoney(r.month_budget)}</td>
                                                                <td className="num">{fmtMoney(r.month_actual)}</td>
                                                                <td className="num">
                                                                  <span className={r.month_variance > 0 ? "admin-dash-var-over" : r.month_variance < 0 ? "admin-dash-var-under" : ""}>
                                                                    {fmtVar(r.month_variance)}
                                                                  </span>
                                                                </td>
                                                                <td className="num">{fmtMoney(r.ytd_actual)}</td>
                                                              </tr>
                                                            );
                                                          })}
                                                        </tbody>
                                                      </table>
                                                    )}
                                                  </div>
                                                </td>
                                              </tr>
                                            )}
                                          </React.Fragment>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                  )}
                                </div>
                              </td>
                            </tr>
                            );
                          })()}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
};

export default ActualsDashboard;
